import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface SidebarItem {
  label: string;
  icon?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}

/** Классы пункта навигации: без цветной полоски слева (только фон + начертание у активного). */
export function sidebarNavItemClassName(active?: boolean) {
  return cn(
    'no-underline outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring)/0.35)] focus-visible:ring-offset-0',
    'flex w-full min-w-0 items-center gap-2 rounded-[var(--ui-radius-control)] px-2.5 py-1.5 text-left text-[13px] leading-snug text-[hsl(var(--ui-muted-foreground))] transition-[background-color,color,font-weight] duration-150',
    'hover:bg-[hsl(var(--ui-muted))] hover:text-[hsl(var(--ui-foreground))]',
    active && 'bg-[hsl(var(--ui-muted))] font-medium text-[hsl(var(--ui-foreground))]'
  );
}

export interface SidebarProps {
  /** Плоский список (legacy); взаимоисключающий с `children`. */
  items?: SidebarItem[];
  /** Секционная разметка (`SidebarSection` + ссылки приложения). */
  children?: React.ReactNode;
  className?: string;
  dataTestId?: string;
}

export function Sidebar({ items, children, className, dataTestId }: SidebarProps) {
  if (children != null) {
    return <nav data-testid={dataTestId} className={cn('flex flex-col gap-0 overflow-y-auto px-2 py-2', className)}>{children}</nav>;
  }
  const list = items ?? [];
  return (
    <nav data-testid={dataTestId} className={cn('space-y-0.5 overflow-y-auto px-2 py-2', className)}>
      {list.map((item) => (
        <button
          key={item.label}
          type="button"
          onClick={item.onClick}
          className={sidebarNavItemClassName(item.active)}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </nav>
  );
}

export interface SidebarSectionProps {
  title?: string;
  className?: string;
  children: React.ReactNode;
  /** Узкая колонка (только иконки): заголовок и аккордеон отключены. */
  collapsed?: boolean;
  /** Раскрыта ли секция (контролируемый режим). Без `onExpandedChange` не используйте. */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Начальное состояние в неконтролируемом режиме. */
  defaultExpanded?: boolean;
  dataTestId?: string;
}

export function SidebarSection({
  title,
  className,
  children,
  collapsed,
  expanded: expandedProp,
  onExpandedChange,
  defaultExpanded = true,
  dataTestId
}: SidebarSectionProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultExpanded);
  const controlled = expandedProp !== undefined;
  const open = controlled ? Boolean(expandedProp) : uncontrolledOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!controlled) {
        setUncontrolledOpen(next);
      }
      onExpandedChange?.(next);
    },
    [controlled, onExpandedChange]
  );

  const showCollapsible = Boolean(title && !collapsed);
  const sectionId = React.useId();
  const panelId = `${sectionId}-panel`;

  return (
    <div
      data-testid={dataTestId}
      className={cn(
        'min-w-0 border-t border-[hsl(var(--ui-border)/0.55)] pt-2.5 first:border-t-0 first:pt-0',
        className
      )}
    >
      {showCollapsible ? (
        <button
          type="button"
          id={`${sectionId}-trigger`}
          className={cn(
            'mb-1 flex w-full min-w-0 items-center justify-between gap-2 rounded-[var(--ui-radius-control)] px-2 py-1.5 text-left text-sm font-semibold text-[hsl(var(--ui-foreground))]',
            'outline-none transition-colors hover:bg-[hsl(var(--ui-muted))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring)/0.35)] focus-visible:ring-offset-0'
          )}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen(!open)}
        >
          <span className="min-w-0 truncate">{title}</span>
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-[hsl(var(--ui-muted-foreground))] transition-transform duration-200',
              open && 'rotate-180'
            )}
            aria-hidden
          />
        </button>
      ) : null}
      {showCollapsible && !open ? null : (
        <div id={showCollapsible ? panelId : undefined} className="flex flex-col gap-px" role={showCollapsible ? 'region' : undefined} aria-labelledby={showCollapsible ? `${sectionId}-trigger` : undefined}>
          {children}
        </div>
      )}
    </div>
  );
}
